import * as React from 'react';
import { Form, Select, Text } from 'react-form';
import { BaseFormProps } from '../../interfaces';
import { Question, QuestionText, QuestionOption, Characteristic, ScoreTypeInconsistent, SurveyVersion, ScoreWeight } from '../../models';
import { CharacteristicDataService, SurveyVersionDataService, QuestionDataService, NotificationService } from '../../services';

class ScoreWeightBreakdownOption {
    questionOptionID: number;
    label: string;
    value: number;
    scoreWeightIndex: number;
    arrayInd: number | undefined;
    option?: QuestionOption;
}

class ScoreWeightBreakdown {
    questionID: number;
    sort: number;
    scoreWeightIndex: number[];
    options: ScoreWeightBreakdownOption[];
}

interface QuestionEditState {
    characteristics: Characteristic[];
    questions: Question[];
    addLanguageId: number;
    surveyIsPublished: boolean;
    selectedQuestionID: number;
}

interface ScoreTypeFormProps extends BaseFormProps {
    surveyVersionId: number;
    notificationService: NotificationService;
}

export class ScoreTypeForm extends React.Component<ScoreTypeFormProps, QuestionEditState> {
    private characteristicDataService = new CharacteristicDataService(this.props.notificationService);
    private surveyVersionDataService = new SurveyVersionDataService(this.props.notificationService);
    private questionDataService = new QuestionDataService(this.props.notificationService);

    constructor(props: ScoreTypeFormProps) {
        super(props);

        this.props.defaultValues.scoreTypeInconsistents = this.props.defaultValues.scoreTypeInconsistents || [];
        this.props.defaultValues.scoreWeights = this.props.defaultValues.scoreWeights || [];
        this.props.defaultValues.characteristicScores = this.props.defaultValues.characteristicScores || [];

        this.state = {
            characteristics: [],
            questions: [],
            addLanguageId: 1,
            surveyIsPublished: false,
            selectedQuestionID: 0
        };
    }

    public componentDidMount() {
        this.surveyVersionDataService.get(this.props.surveyVersionId).then((surveyVersion: SurveyVersion) => {
            this.setState({
                surveyIsPublished: surveyVersion.published !== null ? true : false
            });

            this.characteristicDataService.getAllBySurveyVersion(surveyVersion).then((characteristics: Characteristic[]) => {
                this.setState({
                    characteristics: characteristics
                });

                this.questionDataService.getAllBySurvey(surveyVersion.survey).then((questions: Question[]) => {
                    questions.sort((q1: Question, q2: Question) => {
                        if (q1.sort > q2.sort) {
                            return 1;
                        }
                        return -1;
                    });
                    this.setState({
                        questions: questions
                    });
                    this.forceUpdate();
                });
            });
        });
    }

    private getBreakdown(formValues: any) {

        var breakdown: ScoreWeightBreakdown[] = [];

        // Find unique questions
        formValues.scoreWeights.map((sw: any) => {
            var qID: number = 0;

            if (sw.questionOption) {
                qID = sw.questionOption.questionID;
                var o = breakdown.find((swb: ScoreWeightBreakdown) => {
                    return swb.questionID === qID;
                });
                if (!o) {
                    o = {
                        questionID: qID,
                        sort: sw.sort,
                        options: [],
                        scoreWeightIndex: [ ]
                    };
                    breakdown.push(o);
                }
                if (sw.id) {
                    o.scoreWeightIndex.push(sw.id);
                }                
            }
        });

        // Add question options
        if (this.state.questions) {
            breakdown.map((swb: ScoreWeightBreakdown) => {

                var q = this.state.questions.find(x => {
                    return x.id === swb.questionID;
                });
                if (q !== undefined && q.questionOptions !== undefined) {
                    q.questionOptions.map((x: QuestionOption) => {
                        if (x.questionOptionTexts.length > 0 && x) {
                            var o = new ScoreWeightBreakdownOption();
                            o.option = x;
                            o.questionOptionID = x.id,
                            o.label = x.questionOptionTexts[0].value;
                            o.value = 0;
                            o.scoreWeightIndex = 0;
                            o.arrayInd = undefined;

                            swb.options.push(o);
                        }
                    });
                }
            });
        }

        // Set values
        formValues.scoreWeights.map((sw: any, swInd: number) => {
            // find score weight breakdown
            if (sw.questionOption) {
                var qID = sw.questionOption.questionID;
                var swb = breakdown.find((o: ScoreWeightBreakdown) => {
                    return o.questionID === qID;
                });
                if (swb) {
                    // find question option
                    var qoID = sw.questionOption.id;
                    var opt = swb.options.find((swbo: ScoreWeightBreakdownOption) => {
                        return swbo.questionOptionID === qoID;
                    });
                    if (opt) {
                        opt.value = sw.weight;
                        opt.scoreWeightIndex = sw.id;
                        if (!sw.id) {
                            opt.arrayInd = swInd;
                        }
                    } else {
                        console.log('opt not found', qoID);
                    }
                }
            }
        });

        breakdown = breakdown.sort((b1: ScoreWeightBreakdown, b2: ScoreWeightBreakdown) => {
            if (b1.sort > b2.sort) {
                return 1;
            } else if (b1.sort < b2.sort) {
                return -1;
            }
            return 0;
        });

        return breakdown;
    }

    public render() {
        if (!this.state) {
            return <div />;
        }

        const questions: any = [];
        if (this.state.questions) {
            this.state.questions.sort((q1: Question, q2: Question) => {
                if (q1.sort > q2.sort) {
                    return 1;
                } else if (q1.sort < q2.sort) {
                    return -1;
                }
                return 0;
            }).map((x: Question) => {
                questions.push({
                    label: '#' + x.sort + ' ' + x.questionTexts[0].value,
                    value: x.id,
                    type: x.questionType
                });
            });
        }

        const characteristicOptions: any = [];
        if (this.state.characteristics) {
            this.state.characteristics.map((x: Characteristic) => {
                characteristicOptions.push({
                    label: x.name,
                    value: x.id
                });
            });
        }

        const scoringTypes = [{
            label: 'True False',
            value: 'trueFalse'
        },
        {
            label: 'Likert',
            value: 'likert'
        },
        {
            label: 'True False Inconsistent',
            value: 'trueFalseInconsistent'
        },
        {
            label: 'Likert Inconsistent',
            value: 'likertInconsistent'
        },
        {
            label: 'True False Consistent',
            value: 'trueFalseConsistent'
        },
        {
            label: 'Likert Consistent',
            value: 'likertConsistent'
        },
        {
            label: 'Highest (requires manual database entries)',
            value: 'maxScore'
        }];

        return (
            <Form 
                defaultValues={this.props.defaultValues} 
                onSubmit={submittedValues => {
                    const values = {
                        ...submittedValues,
                        name: submittedValues.name ? submittedValues.name.trim() : submittedValues.name,
                        area: submittedValues.area ? submittedValues.area.trim() : submittedValues.area
                    };
                    this.props.submit(values);
                }}
            >
                {formApi => (

                    <form onSubmit={formApi.submitForm} id="entityForm">
                        {this.state.surveyIsPublished && <div className="pva-alert">
                            <i>This survey version has already been published, so only content changes are allowed.</i>
                        </div>}

                        <fieldset>
                            <div className="form-group">
                                <label htmlFor="name">Name</label>
                                <Text className="form-control" field="name" id="name" />
                            </div>

                            <div className="form-group">
                                <label htmlFor="name">Area</label>
                                <Text className="form-control" field="area" id="area" />
                            </div>

                            <div className="form-group">
                                <label htmlFor="name">Sort</label>
                                <Text className="form-control" field="sort" id="sort" />
                            </div>
                        </fieldset>

                        <fieldset disabled={this.state.surveyIsPublished}>
                            <div className="form-group">
                                <label htmlFor="name">Scoring Type</label>
                                <Select className="form-control" field="scoringType" options={scoringTypes} />
                            </div>

                            <hr />

                            {
                                (formApi.values.scoringType === 'trueFalseInconsistent' || formApi.values.scoringType === 'likertInconsistent' || formApi.values.scoringType === 'trueFalseConsistent' || formApi.values.scoringType === 'likertConsistent') &&
                                <div>
                                    <h3>Inconsistent Scoring</h3>
                                    <i>Uses an individuals statement responses to determine if they consistently answered the same way. Ex: If Statement One is 2 or less but Statement Two is 3 or more, it's inconsistent.</i>
                                    {
                                        formApi.values.scoreTypeInconsistents.map((text: QuestionText, ind: number) => {
                                            var qs = questions.filter((x: any) => {
                                                if ((formApi.values.scoringType === 'trueFalseInconsistent' || formApi.values.scoringType === 'trueFalseConsistent') && x.type === 'binary') {
                                                    return true;
                                                }
                                                if ((formApi.values.scoringType === 'likertInconsistent' || formApi.values.scoringType === 'likertConsistent') && x.type === 'scale') {
                                                    return true;
                                                }
                                                return false;
                                            });
                                            return <div key={ind}>
                                                <div className="row">
                                                    <div className="col-xs-4">
                                                        <div className="form-group">
                                                            <label htmlFor="name">Statement One</label>
                                                            <Select className="form-control" field={['scoreTypeInconsistents', ind, 'questionOneID']} id={'scoreTypeInconsistents' + ind} options={qs} />
                                                        </div>
                                                    </div>
                                                    <div className="col-xs-4">
                                                        <div className="form-group">
                                                            <label htmlFor="name">Statement Two</label>
                                                            <Select className="form-control" field={['scoreTypeInconsistents', ind, 'questionTwoID']} id={'scoreTypeInconsistents' + ind} options={qs} />
                                                        </div>
                                                    </div>
                                                    <div className="col-xs-2">
                                                        <div className="form-group">
                                                            <label htmlFor="name">Weight</label>
                                                            <Text className="form-control" field={['scoreTypeInconsistents', ind, 'weight']} id={'weight' + ind} />
                                                        </div>
                                                    </div>
                                                    <div className="col-xs-2">
                                                        <div className="form-group">
                                                            <label htmlFor="name">&nbsp;</label>
                                                            <a className="btn btn-danger btn-block" onClick={(e) => this.removeInconsistent(formApi.values, ind)}>Remove</a>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>;
                                        })
                                    }

                                    <hr />

                                    <div className="form-group">
                                        <div className="row">
                                            <div className="col-xs-4">
                                                <a className="btn btn-default form-control" onClick={(e) => this.addStatementMatch(formApi.values)}>Add Statement Match</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }

                            {(() => {
                                if (formApi.values.scoringType !== 'trueFalseInconsistent' && formApi.values.scoringType !== 'likertInconsistent' && formApi.values.scoringType !== 'trueFalseConsistent' && formApi.values.scoringType !== 'likertConsistent') {
                                    var breakdown = this.getBreakdown(formApi.values);

                                    return <div>
                                        <h3>Statement Scoring</h3>
                                        <i>Uses an individuals statement responses to calculate this Raw Score.</i>
                                        <hr />

                                        <hr />

                                        {breakdown.map((swb: ScoreWeightBreakdown, ind: number) => {

                                            return <div key={`swb-${swb.questionID}`}>
                                                <div><b>Statement {ind + 1}</b></div>
                                                <div className="row">
                                                    <div className="col-xs-6">
                                                        <div className="form-group">
                                                            <select className="form-control" onChange={(e) => this.changeQuestion(formApi, swb, e)}>
                                                                {
                                                                    this.state.questions.filter((quest: Question) => {
                                                                        // filter out already used statements
                                                                        if (swb.questionID !== quest.id && breakdown.findIndex((swb2: ScoreWeightBreakdown) => {
                                                                            return swb2.questionID === quest.id;
                                                                        }) > -1) {
                                                                            return false;
                                                                        }
                                                                        return (quest.questionType === 'binary' && formApi.values.scoringType === 'trueFalse') || (quest.questionType === 'scale' && formApi.values.scoringType === 'likert');
                                                                    }).map((quest: Question, qInd: number) => {
                                                                        return <option key={`swb-${ind}-qo-sel-${qInd}`} value={quest.id} selected={swb.questionID === quest.id}>
                                                                            #{quest.sort} {quest.questionTexts[0].value}
                                                                        </option>;
                                                                    })
                                                                }
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="col-xs-2">
                                                        <a className="btn btn-default" onClick={(e) => this.moveUp(formApi, swb)}>Up</a>
                                                        <a className="btn btn-default" onClick={(e) => this.moveDown(formApi, swb)}>Down</a>
                                                    </div>
                                                    <div className="col-xs-2">
                                                        <input className="form-control" type="number" step={1} value={swb.sort} onChange={(e) => this.setSort(formApi, swb, e)} />
                                                    </div>
                                                    <div className="col-xs-2 text-right">
                                                        <a className="btn btn-danger" onClick={(e) => this.removeScorewWeights(formApi, swb)}>Remove</a>
                                                    </div>
                                                </div>
                                                <div>
                                                    {swb.options.sort((a: ScoreWeightBreakdownOption, b: ScoreWeightBreakdownOption) => {
                                                        if (formApi.values.scoringType === 'trueFalse') {
                                                            if (a.label > b.label) {
                                                                return -1;
                                                            } else if (a.label < b.label) {
                                                                return 1;
                                                            }
                                                        } else {
                                                            if (a.label > b.label) {
                                                                return 1;
                                                            } else if (a.label < b.label) {
                                                                return -1;
                                                            }
                                                        }
                                                        return 0;
                                                    }).map((swbo: ScoreWeightBreakdownOption, swboInd: number) => {
                                                        return <div className="row" key={`swb-${swb.questionID}-o-${swboInd}`}>
                                                            <div className="col-xs-3 text-right">
                                                                <label style={{paddingTop: '5px'}}>{swbo.label}</label>
                                                            </div>
                                                            <div className="col-xs-9">
                                                                <input className="form-control" type="number" step="0.05" value={swbo.value} onChange={(e) => this.setWeight(formApi, swb, swboInd, e)} />
                                                            </div>
                                                        </div>;
                                                    })}
                                                </div>
                                                <hr />
                                            </div>;
                                        })}

                                        <hr />

                                        <div>
                                            <div className="row">
                                                <div className="col-xs-8">
                                                    <b>Total Statements: {this.getBreakdown(formApi.values).length}</b>
                                                </div>
                                                <div className="col-xs-2">
                                                    <b>Total Points: {this.totalPoints(formApi.values)}</b>
                                                </div>
                                            </div>
                                        </div>
                                    
                                        <hr />

                                        <div className="row">
                                            <div className="col-xs-8">
                                                <select className="form-control" onChange={(e) => this.setState({selectedQuestionID: parseInt(e.target.value, 10)})}>
                                                    <option value={0} selected={this.state.selectedQuestionID === 0}>- Select Question -</option>
                                                    {
                                                        this.state.questions.filter((quest: Question) => {
                                                            // filter out already used statements
                                                            if (breakdown.findIndex((swb: ScoreWeightBreakdown) => {
                                                                return swb.questionID === quest.id;
                                                            }) > -1) {
                                                                return false;
                                                            }
                                                            
                                                            return (quest.questionType === 'binary' && formApi.values.scoringType === 'trueFalse') || (quest.questionType === 'scale' && formApi.values.scoringType === 'likert');
                                                        }).map((q: Question, qInd: number) => {
                                                            return <option key={`qo-sel-${qInd}`} value={q.id} selected={this.state.selectedQuestionID === q.id}>
                                                                #{q.sort} {q.questionTexts[0].value}
                                                            </option>;
                                                        })
                                                    }
                                                </select>
                                            </div>
                                            <div className="col-xs-4">
                                                <a className="btn btn-default" onClick={(e) => this.addScoreWeights(formApi.values)}>Add Statement Score</a>
                                            </div>
                                        </div>
                                        
                                    </div>;
                                } else {
                                    return <div />;
                                }
                            })()
                            }
                        </fieldset>

                        <br />

                        <button type="submit" className="btn btn-primary">Save</button>
                    </form>
                )}
            </Form>
        );
    }

    private changeQuestion(formApi: any, swb: ScoreWeightBreakdown, e: any) {
        var scoreWeights = formApi.values.scoreWeights;

        var qID = parseInt(e.target.value, 10);
        var q = this.state.questions.find((quest: Question) => {
            return quest.id === qID;
        });

        if (q) {
            var weights: number[] = [];

            // does question exists already
            var alreadyExists = false;
            scoreWeights.map((sw: any) => {
                if (sw.questionOption && sw.questionOption.questionID === qID) {
                    // already exists
                    alreadyExists = true;
                }
            });

            if (alreadyExists) {
                this.props.notificationService.addError('Question is already being used');
                this.forceUpdate();
                return;
            }

            // Remove old score weights
            swb.scoreWeightIndex.map((id: number, ind: number) => {
                var swInd = scoreWeights.findIndex((sw: any) => {
                    return sw.id === id;
                });

                weights.push(scoreWeights[swInd].weight);
                scoreWeights.splice(swInd, 1);
            });

            if (swb.scoreWeightIndex.length === 0) {
                var scoreWeightsToRemove: any[] = [];
                swb.options.map((swbo: ScoreWeightBreakdownOption) => {
                    if (swbo.arrayInd) {
                        scoreWeightsToRemove.push(scoreWeights[swbo.arrayInd]);
                    }
                });

                scoreWeightsToRemove.map((sw: any, ind: number) => {
                    var swInd = scoreWeights.findIndex((scoreWeight: any) => {
                        return scoreWeight === sw;
                    });
    
                    weights.push(scoreWeights[swInd].weight);
                    scoreWeights.splice(swInd, 1);
                });
            }

            // Creating new score weights
            if (q.questionOptions) {
                q.questionOptions.map((qo: QuestionOption, ind: number) => {
                    var sw = new ScoreWeight();
                    var data: any = sw;
                    data.questionOption = qo;
                    data.questionOptionID = qo.id;
                    data.sort = swb.sort;
                    data.weight = weights[ind];
                    scoreWeights.push(sw);
                });
            }

            formApi.setValue('scoreWeights', scoreWeights);
            this.forceUpdate();
        }
    }
    private setSort(formApi: any, swb: ScoreWeightBreakdown, e: any) {
        var newSort = parseInt(e.target.value, 10);

        var scoreWeights = formApi.values.scoreWeights;

        scoreWeights.map((sw: any) => {
            if (sw.questionOption && sw.questionOption.questionID === swb.questionID) {
                sw.sort = newSort;
            }
        });

        formApi.setValue('scoreWeights', scoreWeights);
        this.forceUpdate();
    }

    private moveUp(formApi: any, swb: ScoreWeightBreakdown) {
        var scoreWeights = formApi.values.scoreWeights;
        var newSort = swb.sort - 1;
        scoreWeights.map((sw: any) => {
            if (sw.sort === newSort) {
                sw.sort = swb.sort;
            }
        });
        scoreWeights.map((sw: any) => {
            if (sw.questionOption && sw.questionOption.questionID === swb.questionID) {
                sw.sort = newSort;
            }
        });
        formApi.setValue('scoreWeights', scoreWeights);
        this.forceUpdate();
    }

    private moveDown(formApi: any, swb: ScoreWeightBreakdown) {
        var scoreWeights = formApi.values.scoreWeights;
        var newSort = swb.sort + 1;
        scoreWeights.map((sw: any) => {
            if (sw.sort === newSort) {
                sw.sort = swb.sort;
            }
        });
        scoreWeights.map((sw: any) => {
            if (sw.questionOption && sw.questionOption.questionID === swb.questionID) {
                sw.sort = newSort;
            }
        });
        formApi.setValue('scoreWeights', scoreWeights);
        this.forceUpdate();
    }

    private setWeight(formApi: any, swb: any, swboInd: any, e: any) {
        var scoreWeights = formApi.values.scoreWeights;

        var sortedOptions = swb.options.sort((a: ScoreWeightBreakdownOption, b: ScoreWeightBreakdownOption) => {
            if (formApi.values.scoringType === 'trueFalse') {
                if (a.label > b.label) {
                    return -1;
                } else if (a.label < b.label) {
                    return 1;
                }
            } else {
                if (a.label > b.label) {
                    return 1;
                } else if (a.label < b.label) {
                    return -1;
                }
            }
            return 0;
        });

        var arrayInd = sortedOptions[swboInd].arrayInd;
        if (arrayInd) {
            scoreWeights[arrayInd].weight = parseFloat(e.target.value);
        } else {
            var ind = scoreWeights.findIndex((x: any) => {
                return x.id === swb.options[swboInd].scoreWeightIndex;
            });

            var val = parseFloat(e.target.value);
            if (!isNaN(val)) {
                if (ind > -1) {
                    scoreWeights[ind].weight = val;
                } else {
                    // wasn't created before, create it now
                    // questionOptionID
                    var qoID = swb.options[swboInd].questionOptionID;
                    var sw: any = {
                        questionOption: swb.options[swboInd].option,
                        questionOptionID: qoID,
                        weight: val
                    };
                    sw.weight = val;
                    scoreWeights.push(sw);
                }
            }
        }

        formApi.setValue('scoreWeights', scoreWeights);
    }

    private totalPoints(values: any) {
        var breakdown = this.getBreakdown(values);
        var points = 0;
        breakdown.map((swb: ScoreWeightBreakdown) => {
            var largestValue = 0;
            swb.options.map((swbo: ScoreWeightBreakdownOption) => {
                if (swbo.value > largestValue) {
                    largestValue = swbo.value;
                }
            });
            points += largestValue;
        });
        return points;
    }

    private removeScorewWeights(formApi: any, swb: ScoreWeightBreakdown) {
        var scoreWeights = formApi.values.scoreWeights;

        swb.scoreWeightIndex.map((id: number) => {
            var ind = scoreWeights.findIndex((sw: any) => {
                return sw.id === id;
            });
            scoreWeights.splice(ind, 1);
        });
        if (swb.scoreWeightIndex.length === 0) {
            var optionsToRemove: any[] = [];
            swb.options.map((sw: ScoreWeightBreakdownOption) => {
                if (sw.arrayInd) {
                    optionsToRemove.push(scoreWeights[sw.arrayInd]);
                }
            });
            optionsToRemove.map((swbo: ScoreWeightBreakdownOption) => {
                var swInd = scoreWeights.findIndex((sw: any) => {
                    return sw === swbo;
                });
                if (swInd > -1) {
                    scoreWeights.splice(swInd, 1);
                }
            });
        }
        formApi.setValue('scoreWeights', scoreWeights);
        this.forceUpdate();
    }

    private largestSort(values: any) {
        var sort = 0;
        values.scoreWeights.map((sw: any) => {
            if (sw.sort > sort) {
                sort = sw.sort;
            }
        });
        return sort;
    }

    private addScoreWeights(values: any) {
        var q = this.state.questions.find((question: Question) => {
            return question.id === this.state.selectedQuestionID;
        });
        if (q && q.questionOptions) {
            var sort = this.largestSort(values) + 1;
            q.questionOptions.map((qo: QuestionOption) => {
                var sw = new ScoreWeight();
                var data: any = sw;
                data.questionOption = qo;
                data.questionOptionID = qo.id;
                data.weight = 0;
                data.sort = sort;
                values.scoreWeights.push(sw);
            });
            this.setState(
                {
                    selectedQuestionID: 0
                },
                this.forceUpdate);
        }
    }

    private removeInconsistent(values: any, ind: number) {
        if (confirm('Are you sure you want to remove this score weight?')) {
            values.scoreTypeInconsistents.splice(ind, 1);
            this.forceUpdate();
        }
    }

    private addStatementMatch(values: any) {
        var sti = new ScoreTypeInconsistent();
        values.scoreTypeInconsistents.push(sti);
        this.forceUpdate();
    }
}


// WEBPACK FOOTER //
// ./src/components/scoreType/ScoreTypeForm.tsx